export enum SimEventKind {
  OfferOpened = 0, OfferClosed = 1, CardPicked = 2, SegmentPlaced = 3, TowerPlaced = 4, TowerSold = 5, TowerUpgraded = 6,
  RerollUsed = 7, RerollCharged = 8, WavesStarted = 9, StageStarted = 10, StageCleared = 11, EnemySpawned = 12,
  EnemyDamaged = 13, EnemyKilled = 14, EnemyLeaked = 15, TowerFired = 16, BaseDamaged = 17, LevelWon = 18, LevelLost = 19,
  PointsChanged = 20, EnemySlowed = 21, EnemyBurning = 22, EnemyShredded = 23, ChainHit = 24, CardUnpicked = 25, ChestRolled = 26, EnemyHealed = 27, EnemyBoosted = 28, TowerDisabled = 29, BossSpawned = 30, BombDropped = 31,
  TroopSpawned = 32, TroopDied = 33, TroopAttacked = 34, EnemyBlocked = 35, RallyMoved = 36,
  /** The next stage was called early (a = the stage, b = points paid for the countdown left; §4.8.7). */
  StageCalledEarly = 37,
  /** An in-run offer was skipped and paid (a = offer id, b = points; §4.8.7). */
  SkipRewarded = 38,
  /** An enemy spawned wearing an elite affix (a = enemy id, b = affix index; DESIGN §5.1). */
  EliteSpawned = 39,
  /** A Mortar lobbed a shell (a = shell id, b = tower id, c = flight ticks); its landing point is in `LevelSim.shells` (TOWERS_V2). */
  ShellLaunched = 40,
  /** A Mortar shell landed (a = shell id, b = tower id, c = enemies hit); read the point before the tick ends from the event's shell copy in `LevelSim.landedShells`. */
  ShellLanded = 41,
  /** An Arcane Spire's beam locked onto a new target, ramp back to 0 (a = tower id, b = enemy id). */
  BeamStarted = 42,
  /** An Arcane Spire's beam has nothing to hold (a = tower id, b = the enemy it held). */
  BeamStopped = 43,
  /** An enemy came under a Hex Totem's curse (a = enemy id, b = hex tower id, c = bonus in percent). */
  EnemyHexed = 44,
  // M21 enemies (docs/ENEMIES_V2.md): numbered from 60 so the other M21 features can take 45-59 without a clash.
  /** A Summoner stops to cast (a = enemy id, b = cast ticks); its imps appear when the cast ends. */
  SummonStarted = 60,
  /** A Summoner's cast ended (a = summoner id, b = imps called); each imp also has its EnemySpawned (c = 0). */
  ImpsSummoned = 61,
  /** A Burrower was hit and digs in (a = enemy id, b = dig ticks); still targetable until EnemyBurrowed. */
  EnemyDigging = 62,
  /** A Burrower went underground (a = enemy id, b = its dive number, c = cells to travel × 100): untargetable from now. */
  EnemyBurrowed = 63,
  /** A Burrower breaks the surface (a = enemy id, b = surfacing ticks): targetable again from this tick. */
  EnemySurfaced = 64,
  /** A Stunner winds up a shockwave (a = enemy id, b = wind-up ticks, c = radius × 100). */
  StompWindup = 65,
  /** A Stunner's shockwave (a = enemy id, b = towers disabled, c = radius × 100); each tower has its TowerDisabled (c = enemy id). */
  EnemyStomped = 66,
  /** Magic damage was resisted (a = enemy id, b = tower id or 0, c = the damage it did take, floored): the Spellward's runes. */
  MagicResisted = 67,
  /** A stun, freeze, root or Polymorph broke a Summoner's cast or a Stunner's wind-up (a = enemy id, b = the EnemyAct it was in): no imps, no shockwave; its cooldown starts over. */
  CastInterrupted = 68,
  // ---- Tier 4 (M21, docs/TOWERS_V2.md "Tier 4"). Numbered from 80 so the other M21 work can take 45–79.
  /** A special card (Ascend or a skill) was dealt into the open offer (a = offer id, b = CardKind, c = card index). */
  SpecialDealt = 80,
  /** An Ascend card was spent (a = tower archetype, b = branch index, c = towers raised to tier 4). */
  BranchChosen = 81,
  /** A tower rose to tier 4 (a = tower id, b = branch index); a TowerUpgraded with tier 4 follows it. */
  TowerAscended = 82,
  /** A skill card was spent (a = tower archetype, b = skill index). */
  SkillLearned = 83,
  /** A tier-4 skill went off (a = tower id, b = skill index, c = the enemy it centred on, 0 = none). Shockwave,
   *  Thunderclap, Cluster Bomb, Overheat, Volley, Rally Cry, Ember Nova, Rebirth, Polymorph, Banish, Headshot,
   *  Annihilate, Doom Mark, Contagion, Impale, Deep Freeze, Web Snare, Ambush. */
  SkillFired = 84,
  /** An enemy was stunned / frozen / rooted (a = enemy id, b = tower id, c = ticks). */
  EnemyStunned = 85,
  /** A ground zone was laid (a = zone id, b = tower id, c = ZoneKind); read it in `LevelSim.zones`. */
  ZoneCreated = 86,
  /** A ground zone ran out (a = zone id, b = tower id, c = ZoneKind). */
  ZoneExpired = 87,
  /** A Phoenix Roost's phoenix rose (a = tower id, b = 1 when Rebirth's blaze went with it) — read it in `LevelSim.phoenixes`. */
  PhoenixRisen = 88,
  /** A phoenix burned out (a = tower id). */
  PhoenixFell = 89,
  /** A Venom Nest spider leapt onto an enemy (a = spider id, b = enemy id, c = tower id). */
  SpiderLatched = 90,
  /** A spider let go and went home (a = spider id, b = the enemy, c = tower id). */
  SpiderReturned = 91,
  /** A Tesla Coil discharged (a = tower id, b = the first enemy, c = enemies hit); ChainHit events name the others. */
  TeslaDischarged = 92,
  /** An enemy was polymorphed into a sheep (a = enemy id, b = tower id, c = ticks). */
  EnemyPolymorphed = 93,
  /** An enemy was banished back along the road (a = enemy id, b = tower id, c = cells × 100). */
  EnemyBanished = 94,
  /** The fortress was healed (a = amount, b = tower id, c = base HP after). */
  FortressHealed = 95,
  /** An enemy died to an execute (a = enemy id, b = tower id, c = skill index). Its EnemyKilled follows. */
  EnemyExecuted = 96,
  // ---- M36 (docs/RARES.md): immune units and rare spawns. Numbered from 100.
  /** An attack did nothing to an immune unit (a = enemy id, b = tower id or 0, c = the `Immunity` bit); at most once per enemy per tower per second. */
  EnemyImmune = 100,
  /** A rare creature appeared in place of the stage's waves (a = enemy id, b = its chapter, 1-based: the rare index, c = archetype). */
  RareSpawned = 101,
  /** The rare was killed (a = enemy id, b = the banked chest's Rarity, c = archetype); `LevelSim.rareChest` holds the chest. */
  RareKilled = 102,
  /** The rare reached the fortress (a = enemy id, c = archetype): all of the fortress's health, the level is lost. */
  RareEscaped = 103,
  // ---- M40 (docs/RARES.md "The Starfall Wyrm", sim/wyrm.ts).
  /** The Starfall Wyrm came out of the portal (a = enemy id, b = attempt, 1-based: 1 on its first appearance, 2+ on a retry, c = archetype). */
  WyrmSpawned = 104,
  /** The Wyrm was slain (a = enemy id, b = attempt, c = archetype): the app grants its Starfall skin (or the complete-set chest) at once. */
  WyrmKilled = 105,
  /** The Wyrm reached the fortress and flew off (a = enemy id, b = attempt, c = archetype): no damage, no leak; the action pauses for the choice. */
  WyrmFled = 106,
  /** The player paid for another try (a = the new attempt, b = the HP it comes back with, floored: what it fled with under `retryKeepsDamage`, else its full HP): it reappears at the portal (its WyrmSpawned follows). */
  WyrmRetried = 107,
  /** The player let it go (a = attempts made): the choice closes and the run carries on. */
  WyrmReleased = 108,
  // ---- MP-06 pre-battle supplies. Numbered from 110.
  /** The Rally Horn's squad mustered (b = 1) or stood down (b = 0); a = its troops. */
  RallyHorn = 110,
  // ---- Pets v2 (docs/PETS.md, sim/petSystem.ts, sim/wildPet.ts). Numbered from 120.
  /** The pet bit a raider at the flag (a = enemy id, b = the damage, floored; an immune raider takes none: its EnemyImmune follows). */
  PetBit = 120,
  /** Rally Bark (a = fortress troops within its reach, b = its ticks): they deal more and take less until it ends. */
  PetBarked = 121,
  /** Howl (a = raiders stunned, b = the stun's ticks, bosses half, c = the stage): once a wave; each has its EnemyStunned (b = 0). */
  PetHowled = 122,
  /** The wild pet appeared on the grass (a = its skin's index in the rule's `skins` + 1, 0 = none, b = the ticks it stays, c = the stage). */
  WildPetAppeared = 123,
  /** The wild pet ran off untapped (a = skin + 1, c = the stage). */
  WildPetLeft = 124,
  /** The wild pet was tapped and comes down the road (a = enemy id, b = skin + 1, c = archetype); its EnemySpawned comes first. */
  WildPetEngaged = 125,
  /** The wild pet was beaten: caught (a = enemy id, b = skin + 1, c = archetype); its EnemyKilled comes first. The app pays the catch. */
  WildPetCaught = 126,
  /** The wild pet reached the gate and got away (a = enemy id, b = skin + 1, c = archetype): no leak, no damage. */
  WildPetEscaped = 127,
  /** Pets v3, Mend (the axolotl's passive): a fortress troop healed (a = troop id, b = the health it got back, floored). */
  PetMended = 128,
  /** A Move card relocated a tower (M55; a = tower id, b = new x, c = new y). Its old cell is free again. */
  TowerMoved = 129,
}

export interface SimEvent { readonly kind: SimEventKind; readonly a: number; readonly b: number; readonly c: number }

/** Per-tick event list. The host drains it after each step/execute. */
export class EventBuffer {
  private readonly events: SimEvent[] = [];
  get count(): number { return this.events.length; }
  at(i: number): SimEvent { return this.events[i]; }
  all(): readonly SimEvent[] { return this.events; }
  add(kind: SimEventKind, a = 0, b = 0, c = 0): void { this.events.push({ kind, a, b, c }); }
  /**
   * A host-side observer (the Daily Orders tally) that sees each tick's events once, just before they are cleared —
   * command events included, which a per-step drain misses. Never set or read by the sim itself.
   */
  beforeClear: (() => void) | null = null;
  clear(): void { this.beforeClear?.(); this.events.length = 0; }
  has(kind: SimEventKind): boolean { return this.events.some((e) => e.kind === kind); }
}
